import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Scale, 
  Shield, 
  Book, 
  AlertCircle, 
  FileText, 
  Lock, 
  Globe, 
  Mail,
  ChevronDown,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

const TermsOfService = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(null);

  // Navigation helper
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const mainSections = [
    {
      id: 'overview',
      icon: Book,
      title: "Overview",
      content: {
        text: "Sonitrix ('we,' 'us,' or 'our') operates the website sonitrix.com (the 'Service'). These Website Terms of Service ('Terms') govern your use of our Service (defined in Section 1.1). These Terms are a legally binding contract between you and Sonitrix regarding your use of the Service.",
        important: "BY ACCESSING OR USING THE SERVICE, YOU AGREE THAT YOU HAVE READ AND UNDERSTOOD, AND, AS A CONDITION TO YOUR USE OF THE SERVICE, YOU AGREE TO BE BOUND BY THESE TERMS. If you are not eligible, or do not agree to the Terms, then you do not have our permission to use the Service. YOUR USE OF THE SERVICE, AND SONITRIX'S PROVISION OF THE SERVICE TO YOU, CONSTITUTES AN AGREEMENT BY SONITRIX AND YOU TO BE BOUND BY THESE TERMS.",
        arbitrationNotice: "Arbitration NOTICE. Except for certain kinds of disputes described in Section 14, you agree that disputes arising under these Terms will be resolved by binding, individual arbitration, and BY ACCEPTING THESE TERMS, YOU AND SONITRIX ARE EACH WAIVING THE RIGHT TO A TRIAL BY JURY OR TO PARTICIPATE IN ANY CLASS ACTION OR REPRESENTATIVE PROCEEDING. YOU AGREE TO GIVE UP YOUR RIGHT TO GO TO COURT to assert or defend your rights under this contract (except for matters that may be taken to small claims court). Your rights will be determined by a NEUTRAL ARBITRATOR and NOT a judge or jury."
      }
    },
    {
      id: 'definitions',
      icon: FileText,
      title: "1. Definitions",
      content: {
        subsections: [
          {
            id: '1.1',
            title: "1.1 Service Definition",
            content: "As used throughout these Terms, 'Service' means all portions of the website located at sonitrix.com (including all related subdomains), whether public-facing or accessible behind a log-in, and all features, functionality, content, and materials available on or through such website."
          },
          {
            id: '1.2',
            title: "1.2 AI Platform",
            content: "The Service provides you with general information about our products and services and includes a platform that is accessible through unique access credentials designed to enable users to build and deploy AI models with relative speed and ease (such platform, the 'AI Platform')."
          }
        ]
      }
    },
    {
      id: 'eligibility',
      icon: Shield,
      title: "2. Eligibility",
      content: {
        text: "You must be at least 18 years old to use the Service. By agreeing to these Terms, you represent and warrant to us that:",
        subsections: [
          {
            id: '2.1',
            content: "(a) either you are at least 18 years of age or have been authorized to use the Service by your parent or legal guardian who is at least 18 years of age and who will supervise your use of the Service;"
          },
          {
            id: '2.2',
            content: "(b) you have not previously been suspended or removed from the Service;"
          },
          {
            id: '2.3',
            content: "(c) your registration and your use of the Service is in compliance with any and all applicable laws and regulations."
          }
        ],
        additional: "If you are an entity, organization, or company, the individual accepting these Terms on your behalf represents and warrants that they have the authority to bind you to these Terms and you agree to be bound by these Terms."
      }
    },
    {
      id: 'accounts',
      icon: Lock,
      title: "3. Accounts and Registration",
      content: {
        text: "To access certain features of the Service, including the AI Platform, you must register for an account. When you register for an account, you may be required to provide us with some information about yourself. You agree that the information you provide to us is accurate and that you will keep it accurate and up-to-date at all times. You are solely responsible for maintaining the confidentiality of your account and any access credentials that you need to access the account, and you accept responsibility for all activities that occur under your account. If you believe that your account is no longer secure, then you must immediately notify us at contact@sonitrix.com."
      }
    },
    {
      id: 'licenses',
      icon: FileText,
      title: "4. Licenses",
      content: {
        subsections: [
          {
            id: '4.1',
            title: "4.1 Limited License",
            content: "Subject to your complete and ongoing compliance with these Terms, Sonitrix grants you, solely for your personal use, a limited, revocable, non-exclusive, non-transferable, non-sublicensable, and worldwide license to access and use the Service solely through the permitted functionalities on the Service and for your non-commercial, informational purposes only."
          },
          {
            id: '4.2',
            title: "4.2 License Restrictions",
            content: "Except and solely to the extent such a restriction is impermissible under applicable law, you may not: (a) reproduce, distribute, publicly display, or publicly perform the Service; (b) make modifications to the Service; (c) commercially exploit the Service in any manner; or (d) interfere with or circumvent any feature of the Service, including any security or access control mechanism. If you are prohibited under applicable law from using the Service, you may not use it."
          },
          {
            id: '4.3',
            title: "4.3 Feedback",
            content: "If you choose to provide input or suggestions regarding problems with or proposed modifications or improvements to any aspect of the Service ('Feedback'), then you hereby grant Sonitrix an unrestricted, perpetual, irrevocable, non-exclusive, fully-paid, royalty-free right to exploit the Feedback in any manner and for any purpose, without any limitations, credit, or fees due to you whatsoever."
          },
          {
            id: '4.4',
            title: "4.4 AI Platform",
            content: "Notwithstanding anything in these Terms to the contrary and for the avoidance of doubt, this Section 4 does not apply to the AI Platform and any results or output derived or generated from use or operation of that platform (including any AI models). Any rights that we may grant you to the same will be set forth in the applicable Additional Terms."
          }        
        ]
      }
    },
    {
      id: 'ownership',
      icon: Shield,
      title: "5. Ownership; Proprietary Rights",
      content: {
        text: "The Service is owned and operated by Sonitrix. The visual interfaces, graphics, design, compilation, information, data, computer code (including source code or object code), products, software, services, and all other elements of the Service ('Materials') are protected by intellectual property and other laws. As between you and Sonitrix, the Materials are the sole property of Sonitrix. Except as expressly authorized by Sonitrix, you may not make use of the Materials. Sonitrix reserves all rights to the Materials not granted expressly in these Terms."
      }
    },
    {
      id: 'indemnity',
      icon: Shield,
      title: "11. Indemnity",
      content: {
        text: "To the fullest extent permitted by law, you are responsible for your use of the Service, and you will defend and indemnify Sonitrix and its officers, directors, employees, consultants, affiliates, subsidiaries, and agents from and against every claim brought or threatened by a third party, and any related liability, damage, loss, and expense, including reasonable attorneys' fees and costs, arising out of or connected with: (a) your unauthorized use of, or misuse of, the Service; (b) your violation of any portion of these Terms, any representation, warranty, or agreement referenced in these Terms, or any applicable law or regulation; (c) your violation of any third-party right, including any intellectual property right or publicity, confidentiality, other property, or privacy right; or (d) any dispute or issue between you and any third party. We reserve the right, at our own expense, to assume the exclusive defense and control of any matter otherwise subject to indemnification by you (without limiting your indemnification obligations with respect to that matter), and in that case, you agree to cooperate with our defense of those claims."
      }
    },
    {
      id: 'disclaimers',
      icon: AlertCircle,
      title: "12. Disclaimers; No Warranties",
      content: {
        text: "WITHOUT LIMITING ANY OTHER TERMS IN THIS DOCUMENT AND TO THE FULLEST EXTENT PERMITTED BY LAW:",
        subsections: [
          {
            id: '12.1',
            content: "THE SERVICE IS PROVIDED 'AS IS' AND ON AN 'AS AVAILABLE' BASIS. SONITRIX AND ITS CURRENT AND FUTURE AFFILIATES (COLLECTIVELY, THE 'SONITRIX ENTITIES') DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, RELATING TO THE SERVICE, INCLUDING: (A) ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, QUIET ENJOYMENT, OR NON-INFRINGEMENT; AND (B) ANY WARRANTY ARISING OUT OF COURSE OF DEALING, USAGE, OR TRADE. THE SONITRIX ENTITIES DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR FREE OF ERRORS, VIRUSES, OR OTHER HARMFUL COMPONENTS, AND THE SONITRIX ENTITIES DO NOT WARRANT THAT ANY OF THOSE ISSUES WILL BE CORRECTED."
          },
          {
            id: '12.2',
            content: "THE SONITRIX ENTITIES ARE NOT RESPONSIBLE FOR ANY DAMAGE OF ANY KIND THAT MAY RESULT FROM THE SERVICE OR YOUR DEALING WITH ANY OTHER SERVICE USER."
          }
        ]
      }
    },
    {
      id: 'limitation-liability',
      icon: Scale,
      title: "13. Limitation of Liability",
      content: {
        subsections: [
          {
            id: '13.1',
            content: "TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL THE SONITRIX ENTITIES BE LIABLE TO YOU FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES (INCLUDING DAMAGES FOR LOSS OF PROFITS, GOODWILL, OR ANY OTHER INTANGIBLE LOSS) ARISING OUT OF OR RELATING TO YOUR ACCESS TO OR USE OF, OR YOUR INABILITY TO ACCESS OR USE, THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT ANY SONITRIX ENTITY HAS BEEN INFORMED OF THE POSSIBILITY OF DAMAGE."
          },
          {
            id: '13.2',
            content: "TO THE FULLEST EXTENT PERMITTED BY LAW, THE AGGREGATE LIABILITY OF THE SONITRIX ENTITIES TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE USE OF OR ANY INABILITY TO USE THE SERVICE, WHETHER IN CONTRACT, TORT, OR OTHERWISE, IS LIMITED TO $100."
          }
        ]
      }
    }
  ];

  const TableOfContents = () => (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-8">
      <h2 className="text-xl font-semibold mb-4">Table of Contents</h2>
      <nav className="space-y-2">
        {mainSections.map((section) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className="block text-left text-gray-600 hover:text-violet-600 transition-colors"
          >
            {section.title}
          </button>
        ))}
      </nav>
    </div>
  );

  return (
    <section className="py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header Section */}
        <div className="space-y-6 mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/10 to-indigo-500/10">
            <Scale className="w-5 h-5 text-violet-600 mr-2" />
            <span className="text-sm font-medium text-violet-600">Terms of Service</span>
          </div>
          
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
            Terms of Service
          </h1>
          
          <p className="text-lg text-gray-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertCircle className="w-5 h-5" />
              <p className="font-medium">Important Notice</p>
            </div>
            <p className="mt-2 text-amber-700">
              By using our Service, you agree to these terms. Please read them carefully before proceeding.
            </p>
          </div>
        </div>

        {/* Table of Contents */}
        <TableOfContents />

        {/* Main Content */}
        <div className="space-y-8">
          {mainSections.map((section) => (
            <div 
              key={section.id}
              id={section.id} 
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-violet-500/10">
                  <section.icon className="w-5 h-5 text-violet-600" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">{section.title}</h2>
              </div>

              <div className="space-y-4">
                {section.content.text && (
                  <p className="text-gray-600">{section.content.text}</p>
                )}

                {section.content.important && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 my-4">
                    <p className="text-violet-900 font-medium">{section.content.important}</p>
                  </div>
                )}

                {section.content.arbitrationNotice && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 my-4">
                    <p className="text-gray-900">{section.content.arbitrationNotice}</p>
                  </div>
                )}

                {section.content.subsections && (
                  <div className="space-y-4">
                    {section.content.subsections.map((subsection) => (
                      <div key={subsection.id} id={subsection.id} className="ml-4">
                        <h3 className="font-medium text-gray-900 mb-2">{subsection.title}</h3>
                        <p className="text-gray-600">{subsection.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TermsOfService;
